import FocusManager, { IFocusableItemProps } from "@components/focusManager";
import Field from "@components/inputs/field";
import Switch from "@components/inputs/switch";
import { SwitchType } from "@components/inputs/switch/Switch";
import { CompanyEntity, CzNaceEntity, EntitySetName, EntityTypeName, IBillingPricingEntity, ICzNaceEntity } from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    BillingPricingCode,
    CurrencyCode,
    FeatureCode,
    ProductCode,
    PurchaseStatusCode,
    SubscriptionTypeCode,
    TypeOfBusinessCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { WithOData, withOData } from "@odata/withOData";
import { getPayrollRemainingTrialPeriodDays, getRemainingTrialPeriodDays, isInPayrollTrial, isInTrial } from "@pages/admin/subscriptions/Subscriptions.utils";
import { isDefined, isNotDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import i18next from "i18next";
import React, { useCallback } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AppContext, IAppContext } from "../../contexts/appContext/AppContext.types";
import { LabelStatus } from "../../enums";
import { Title2_2 } from "../../global.style";
import { KeyName } from "../../keyName";
import TestIds from "../../testIds";
import Currency from "../../types/Currency";
import { getUtcDayjs } from "../../types/Date";
import { FormStorage } from "../../views/formView/FormStorage";
import { ICompanyExtendedEntity, ICompanyFormCustomData } from "./Company.types";
import { companyTypeSettingsPath, getCurrentCzNaceFilter, hasGrantPath, hasPayrollEvidencePath } from "./Company.utils";
import {
    AccountedFromInfo,
    AdditionalEmphasized,
    AdditionalSettings,
    CompanyTypeWrapper,
    EmphasisedBackgroundStyled,
    EmphasizedText,
    SettingsCard,
    SettingsHeader,
    SettingTilesWrapper,
    SubText,
    TopText
} from "./CompanyTypeSettings.styles";

interface IProps extends WithTranslation, WithOData {
    storage: FormStorage<ICompanyExtendedEntity, ICompanyFormCustomData>;
}

const GRANT_DISABLED_CODE = "CannotDisableGrantsModuleTooltip";

class CompanyTypeSettings extends React.Component<IProps> {
    static contextType = AppContext;

    constructor(props: IProps, context: IAppContext) {
        super(props, context);

        if (!props.storage.data.entity.AccountingCode) {
            const accountingBc = this.props.storage.data.bindingContext.navigate(CompanyEntity.Accounting);
            // default value
            props.storage.setValue(accountingBc, AccountingCode.AccountingForBusiness);
        }

        // automatically register on model
        this.props.storage.addRef(this, this.props.storage.data.bindingContext.navigate(companyTypeSettingsPath));
    }

    isNew = (): boolean => {
        return this.props.storage.data.bindingContext.isNew();
    };

    get productPrices(): IBillingPricingEntity[] {
        return this.props.storage.getCustomData().productPrices;
    }

    get isInTrial(): boolean {
        const context = this.context as IAppContext;
        const subscription = context.getData().subscription;

        return isInTrial(subscription);
    }

    get isInPayrollTrial(): boolean {
        const context = this.context as IAppContext;
        const subscription = context.getData().subscription;

        return isInPayrollTrial(subscription);
    }

    get isPurchused(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.PurchaseStatusCode === PurchaseStatusCode.Purchased;
    }

    get isFree(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.SubscriptionTypeCode === SubscriptionTypeCode.Free || subscription?.SubscriptionTypeCode === SubscriptionTypeCode.EDU;
    }

    get isIndividual(): boolean {
        const subscription = this.context.getData().subscription;
        return subscription?.Tenant?.ProductCode === ProductCode.EvalaForCompanies;
    }

    get company(): ICompanyExtendedEntity {
        return this.props.storage.getEntity();
    }

    handleTileClick = (tileId: string): void => {
        const accountingBc = this.props.storage.data.bindingContext.navigate(CompanyEntity.Accounting);

        this.props.storage.setValue(accountingBc, tileId as AccountingCode);
        if (tileId === AccountingCode.CashBasisAccounting) {
            this.props.storage.setValueByPath(CompanyEntity.TypeOfBusiness, TypeOfBusinessCode.NaturalPerson);
        }
        if (tileId === AccountingCode.AccountingForNonprofitOrganizations) {
            this.props.storage.setValueByPath(CompanyEntity.TypeOfBusiness, TypeOfBusinessCode.LegalPerson);
        }

        if (this.isNew()) {
            this.props.storage.setValueByPath(hasGrantPath, tileId === AccountingCode.AccountingForNonprofitOrganizations);
        }

        this.props.storage.refresh(true);
    };

    handleGrantSwitchChange = (checked: boolean): void => {
        this.props.storage.setValueByPath(hasGrantPath, checked);
        this.props.storage.refresh();
    }

    handlePayrollSwitchChange = async (checked: boolean): Promise<void> => {
        const storage = this.props.storage;
        storage.setValueByPath(hasPayrollEvidencePath, checked);
        if (checked && !storage.data.origEntity?.DefaultStatutoryInsuranceCompanyRate) {
            const rate = await this.getCzNaceRate();
            if (isDefined(rate)) {
                storage.setValueByPath(CompanyEntity.DefaultStatutoryInsuranceCompanyRate, rate);
            }
        }
        storage.refresh();
    };

    getCzNaceRate = async (): Promise<number | undefined> => {
        const { storage } = this.props;
        const czNace = storage.data.entity.CzNace;

        if (isDefined(czNace?.Rate) || isNotDefined(czNace?.MpsvCode)) {
            return czNace?.Rate;
        }

        const mpsvCodeFilter = `${CzNaceEntity.MpsvCode} eq '${czNace.MpsvCode}'`;
        const result = await storage.oData.getEntitySetWrapper(EntitySetName.CzNaces)
            .query()
            .filter(`(${getCurrentCzNaceFilter()}) and ${mpsvCodeFilter}`)
            .select(CzNaceEntity.Rate)
            .top(1)
            .fetchData<ICzNaceEntity[]>();

        return result.value?.[0]?.Rate;
    };

    getProductPrice = (accountingTypeCode: AccountingCode): number => {
        const productPrices = this.productPrices;
        const productPrice = productPrices.find((product) => product.AccountingCode === accountingTypeCode);

        return productPrice?.Price;
    };

    getPrice = (showZero = true, accountingCode = this.company.AccountingCode, suffix = this.props.t("Companies:CompanyTypeSettings.WithoutVat"), showTotalPrice = false) => {
        const { storage } = this.props;
        const isZero = this.isFree || (this.isInTrial && !this.isPurchused);
        if (isZero && !showZero) {
            return i18next.t("Companies:Form.Free");
        }

        let price = isZero ? 0 : this.getProductPrice(accountingCode) || 0;

        const hasPayroll = storage.getValueByPath(hasPayrollEvidencePath);
        if (hasPayroll && showTotalPrice) {
            price += this.getPayrollPrice();
        }

        const formattedPrice = this.formatAmount(price);
        return price === 0 ? `${formattedPrice}` : `${formattedPrice} ${suffix}`;
    }

    formatAmount = (amount: number): string => {
        return Currency.format(amount, {
            currency: CurrencyCode.CzechKoruna,
            maximumFractionDigits: 0
        });
    };

    renderEditTile = (): React.ReactElement => {
        const company = this.company;
        const prefix = this.isIndividual && this.isPurchused ? `${i18next.t("Subscriptions:ProductSelection.User")} / ` : "";

        return (
            <CompanyTypeWrapper>
                <Title2_2 data-testid={TestIds.CompanyAccountingType}>
                    {getEnumDisplayValue(EntityTypeName.Accounting, company.AccountingCode)}
                </Title2_2>
                <AdditionalEmphasized>
                    {prefix}
                    {this.getPrice(false, this.company.AccountingCode, i18next.t("Companies:CompanyTypeSettings.Monthly"))}
                    {this.isInTrial &&
                        <AccountedFromInfo>&nbsp;|&nbsp;{this.getMonthlySubtext(false)}</AccountedFromInfo>}
                </AdditionalEmphasized>
            </CompanyTypeWrapper>
        );
    };

    getMonthlySubtext = (showIndividual = true, isPayroll = false): string => {
        const context = this.context as IAppContext;
        const subscription = context.getData().subscription;

        const prefix = showIndividual && this.isIndividual && this.isPurchused ? `${i18next.t("Subscriptions:ProductSelection.User")} / ` : "";

        const _text = (t: string) => {
            return `${prefix}${t}`;
        };

        if (this.isFree) {
            return i18next.t("Companies:CompanyTypeSettings.Unlimited");
        }

        if (this.isInTrial) {
            if (this.isPurchused) {
                const remainingDaysCount = getRemainingTrialPeriodDays(subscription);
                const dateTrialEnd = getUtcDayjs().add(remainingDaysCount, "day").toDate();

                const formatedDate = dateTrialEnd.toLocaleDateString(i18next.language, {
                    day: "numeric",
                    month: "numeric"
                });

                return _text(this.props.t("Companies:CompanyTypeSettings.MonthlyFrom", { day: formatedDate }));
            } else {
                return _text(i18next.t("Companies:Form.AccountedAfterPurchase"));
            }
        } else if (this.isInPayrollTrial && isPayroll) {
            const remainingDaysCount = getPayrollRemainingTrialPeriodDays(subscription);
            const dateTrialEnd = getUtcDayjs().add(remainingDaysCount, "day").toDate();

            const formatedDate = dateTrialEnd.toLocaleDateString(i18next.language, {
                day: "numeric",
                month: "numeric"
            });

            return _text(this.props.t("Companies:CompanyTypeSettings.MonthlyFrom", { day: formatedDate }));
        }

        return _text(this.props.t("Companies:CompanyTypeSettings.Monthly"));
    };

    renderNewTile = (): React.ReactElement => {
        const accountingTypes = Object.values(AccountingCode);

        return (
            <FocusManager>
                {({ itemProps, wrapperProps }) => {
                    return (
                        <SettingTilesWrapper {...wrapperProps}
                                             ref={wrapperProps.ref as React.RefObject<HTMLDivElement>}
                                             $colCount={accountingTypes.length}
                        >
                            {accountingTypes.map((accountingTypeCode, index) => {
                                return (
                                    <SettingTile key={accountingTypeCode}
                                                 id={accountingTypeCode}
                                                 topText={getEnumDisplayValue(EntityTypeName.Accounting, accountingTypeCode)}
                                                 isSelected={accountingTypeCode === this.company.AccountingCode}
                                                 focusItemProps={itemProps}
                                                 onSelect={this.handleTileClick}
                                                 emphasizedText={this.getPrice(false, accountingTypeCode, "")}
                                                 subText={this.getMonthlySubtext()}/>
                                );
                            })}
                        </SettingTilesWrapper>
                    );
                }}
            </FocusManager>
        );
    };

    isGrantDisabled = memoizeOne(() => {
        return Object.values(
            this.props.storage?.data?.metadata?.metadata?.DisabledPropertyRules ?? {}
        ).some(rules =>
            rules.some(rule => rule.ErrorCode === GRANT_DISABLED_CODE)
        );
    }, () => {
        const storage = this.props.storage as FormStorage;
        return [storage?.data?.metadata?.metadata?.DisabledPropertyRules];
    });

    renderGrantSwitch = () => {
        if (this.props.storage.getValueByPath(CompanyEntity.AccountingCode) === AccountingCode.CashBasisAccounting) {
            return null;
        }

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalGrantsSettings}>
                <Field isDisabled={this.isGrantDisabled()} labelStatus={LabelStatus.Removed}
                       isWithoutRightMargin
                       disabledMessage={i18next.t("Error:CannotDisableGrantsModuleTooltip")}>
                    <Switch checked={this.props.storage.getValueByPath(hasGrantPath)}
                            type={SwitchType.Icons}
                            isDisabled={this.isGrantDisabled() || !this.props.storage.getCustomData().hasPermission}
                            label={this.props.t("Companies:CompanyTypeSettings.GrantEvidence")}
                            onChange={this.handleGrantSwitchChange}
                    />
                </Field>
                <AdditionalEmphasized data-testid={TestIds.Text}
                    style={{ marginLeft: "20px" }}>
                    {this.props.t("Companies:Form.Free")}
                </AdditionalEmphasized>
            </AdditionalSettings>
        );

    }

    getPayrollPrice = (): number => {
        if (this.isFree) {
            return 0;
        }
        return this.isIndividual ? this.productPrices.find(p => p.Code === BillingPricingCode.CZPayrollForIndividuals)?.Price || 0 : this.productPrices.find(p => p.Code === BillingPricingCode.CZPayroll)?.Price || 0;
    }

    renderPayrollSwitch = (): React.ReactElement => {
        const { storage } = this.props;
        const isCashBasis = storage.getValueByPath(CompanyEntity.AccountingCode) === AccountingCode.CashBasisAccounting;
        if (!this.context.isFeatureSwitchEnabled(FeatureCode.Payroll) || isCashBasis) {
            return null;
        }
        const price = this.getPayrollPrice();
        const showFree = this.isFree || (this.isInTrial && !this.isPurchused) || price === 0;

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalPayrollSettings}>
                <Switch checked={storage.getValueByPath(hasPayrollEvidencePath)}
                        type={SwitchType.Icons}
                        isDisabled={!storage.getCustomData().hasPermission}
                        label={this.props.t("Companies:CompanyTypeSettings.PayrollEvidence")}
                        onChange={this.handlePayrollSwitchChange}
                />
                <AdditionalEmphasized data-testid={TestIds.Text}
                    style={{ marginLeft: "20px" }}>
                    {showFree ? this.props.t("Companies:Form.Free") : `+${price} Kč `}
                    {price !== 0 && <AccountedFromInfo>&nbsp;|&nbsp;{this.getMonthlySubtext(false, true)}</AccountedFromInfo>}
                </AdditionalEmphasized>
            </AdditionalSettings>
        );
    };

    shouldShowAdditionalInfoText = () => {
        return !(this.isInTrial && !this.isPurchused);
    }

    renderAdditionalInfoText = () => {
        if (this.isFree) {
            return null;
        }

        const text = this.isIndividual ? this.props.t("Companies:CompanyTypeSettings.SummerPriceIndividual") :
            this.props.t("Companies:CompanyTypeSettings.SummedPrice");

        return (
            <AdditionalSettings data-testid={TestIds.AdditionalInfo}>
                {text}
                <AdditionalEmphasized>
                    {this.getPrice(undefined, undefined, undefined, true)}.
                </AdditionalEmphasized>
            </AdditionalSettings>
        );
    }

    render() {
        const productPrices = this.productPrices;
        const shouldShowAdditionalInfoText = this.shouldShowAdditionalInfoText();

        return (
            <EmphasisedBackgroundStyled largeBottomPadding={this.isNew() && !shouldShowAdditionalInfoText}>
                {this.isNew() &&
                    <SettingsHeader>
                        {this.props.t("Companies:CompanyTypeSettings.Title")}
                    </SettingsHeader>
                }

                {productPrices.length &&
                    (
                        <>
                            {this.isNew() ? this.renderNewTile() : this.renderEditTile()}
                            <>
                                {this.renderPayrollSwitch()}
                                {this.renderGrantSwitch()}
                                {shouldShowAdditionalInfoText && this.renderAdditionalInfoText()}
                            </>
                        </>
                    )
                }
            </EmphasisedBackgroundStyled>
        );
    }
}

export default withTranslation(["Companies", getEnumNameSpaceName(EntityTypeName.Accounting)])(withOData(CompanyTypeSettings));

interface ISettingTileProps {
    id: string;
    topText: string;
    emphasizedText: string;
    subText: string;
    isSelected?: boolean;
    focusItemProps: IFocusableItemProps;
    onSelect: (id: string) => void;
}

const SettingTile: React.FC<ISettingTileProps> = (props) => {
    const { id, onSelect } = props;
    const fnHandleClick = useCallback(() => {
        onSelect(id);
    }, [id, onSelect]);

    const fnHandleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === KeyName.Enter || e.key === KeyName.Space) {
            e.preventDefault();
            onSelect(id);
        }
    }, [id, onSelect]);


    return (
        <SettingsCard $selected={props.isSelected}
                      {...props.focusItemProps}
                      onClick={fnHandleClick}
                      onKeyDown={fnHandleKeyDown}
                      data-testid={TestIds.CompanyAccountingTypeCard}>
            <TopText data-testid={TestIds.Title}>
                {props.topText}
            </TopText>
            <EmphasizedText data-testid={TestIds.Price}>
                {props.emphasizedText}
            </EmphasizedText>
            <SubText data-testid={TestIds.Subtitle}>
                {props.subText}
            </SubText>
        </SettingsCard>
    );
};
